// Core
import { useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Components
import { Button, Chip, DataTable } from "components/AtomsV2";
import { Icon } from "components/ShellV2";
import HelpFloatingButtonV2 from "components/HelpFloatingButtonV2";
import VersionManagementButtons from "components/VersionManagementButtons";

// Redux
import { useDispatch, useSelector } from "react-redux";
import { openNotificationPopup, openWarningAlert } from "state/application/applicationSlice";
import { selectCurrentCommunity } from "state/communities/communitiesSlice";
import {
  useDeleteContractByIdMutation,
  useGetContractsByMeterIdQuery,
} from "state/contracts/contractsApiSlice";
import { useGetMetersByCommunityIdQuery } from "state/meters/metersApiSlice";

// Utils
import { ciclesTypes, contractTypes } from "utils/constants/index";
import { formatDate } from "utils/time";

const resolveLabel = (arr, value, t, keyPrefix) => {
  const found = arr.find((o) => o.value === value);
  if (!found) return value;
  return t(`${keyPrefix}.${value}`, { defaultValue: found.label });
};

function ContractInformation() {
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { t } = useTranslation(["contracts", "meters", "common"]);

  const community = useSelector(selectCurrentCommunity);

  // READ meter (for header info)
  const { meter } = useGetMetersByCommunityIdQuery(
    { communityId: community.id, excludeDeleted: "true" },
    {
      selectFromResult: ({ data }) => ({ meter: data?.entities[id] }),
    }
  );

  // READ contracts
  const { data: contracts = [] } = useGetContractsByMeterIdQuery({
    communityId: community.id,
    meterId: id,
  });

  // VIEW contract
  const onViewContractClicked = async (contractId) => {
    navigate(`/communities/${community.id}/infrastructure/contracts/${contractId}`, {
      state: { meterId: id, action: "view" },
    });
  };

  const onEditContractClicked = async (contractId) => {
    navigate(`/communities/${community.id}/infrastructure/contracts/${contractId}`, {
      state: { meterId: id, action: "edit", meterIdentifier: meter?.identifier },
    });
  };

  // DELETE contract
  const [deleteContract] = useDeleteContractByIdMutation();
  const onDeleteContractClicked = async (contractId) => {
    const action = async () => {
      try {
        await deleteContract({
          contractId,
          meterId: id,
          communityId: community.id,
        }).unwrap();
      } catch (err) {
        dispatch(
          openNotificationPopup({
            error: err,
          })
        );
      }
    };

    dispatch(
      openWarningAlert({
        confirmText: t("confirm.delete", { ns: "contracts" }),
        confirmAction: action,
      })
    );
  };

  const backToMeters = () => navigate(`/communities/${community.id}/infrastructure/meters`);

  const rows = useMemo(
    () =>
      contracts.map((c) => ({
        id: c.id,
        validity: `${formatDate(c.start_date || c.validity)} – ${
          c.end_date ? formatDate(c.end_date) : "…"
        }`,
        validityStart: c.start_date || c.validity || "",
        buy_type: c.buy_type,
        sell_type: c.sell_type,
        voltage_level: c.voltage_level,
        tariff_cycle: c.tariff_cycle,
        with_holidays: !!c.with_holidays,
        contracted_power: c.contracted_power,
      })),
    [contracts]
  );

  const columns = [
    {
      key: "validity",
      label: t("list.table.columns.validity"),
      sortValue: (row) => row.validityStart,
      render: (v) => (
        <div className="rec-cell-member">
          <div
            className="rec-av-sm"
            style={{ background: "var(--accent-soft)", color: "var(--accent-deep)" }}
          >
            <Icon name="receipt_long" style={{ fontSize: 18 }} />
          </div>
          <b>{v}</b>
        </div>
      ),
    },
    {
      key: "buy_type",
      label: t("list.table.columns.buy_type"),
      render: (v) => <Chip tone="teal">{resolveLabel(contractTypes, v, t, "options.types")}</Chip>,
    },
    {
      key: "sell_type",
      label: t("list.table.columns.sell_type"),
      render: (v) => <Chip tone="info">{resolveLabel(contractTypes, v, t, "options.types")}</Chip>,
    },
    { key: "voltage_level", label: t("list.table.columns.voltage_level") },
    {
      key: "tariff_cycle",
      label: t("list.table.columns.tariff_cycle"),
      render: (v) => resolveLabel(ciclesTypes, v, t, "options.cicles"),
    },
    {
      key: "with_holidays",
      label: t("list.table.columns.with_holidays"),
      render: (v) =>
        v ? (
          <Chip tone="green">{t("yes", { ns: "common", defaultValue: "Sim" })}</Chip>
        ) : (
          <Chip tone="gray">{t("no", { ns: "common", defaultValue: "Não" })}</Chip>
        ),
    },
    {
      key: "contracted_power",
      label: t("list.table.columns.contracted_power"),
      align: "right",
      render: (v) => <span className="tabular-nums">{v}</span>,
    },
    {
      key: "_actions",
      label: "",
      sort: false,
      align: "right",
      render: (v, row) => (
        <div className="rec-row-actions">
          <button
            type="button"
            className="rec-icon-btn"
            title={t("list.actions.view")}
            onClick={(e) => {
              e.stopPropagation();
              onViewContractClicked(row.id);
            }}
          >
            <Icon name="visibility" />
          </button>
          <button
            type="button"
            className="rec-icon-btn"
            title={t("list.actions.edit")}
            onClick={(e) => {
              e.stopPropagation();
              onEditContractClicked(row.id);
            }}
          >
            <Icon name="edit" />
          </button>
          <button
            type="button"
            className="rec-icon-btn"
            title={t("list.actions.delete")}
            onClick={(e) => {
              e.stopPropagation();
              onDeleteContractClicked(row.id);
            }}
          >
            <Icon name="delete" />
          </button>
        </div>
      ),
    },
  ];

  const instLabel = meter?.installation_type
    ? t(`table.filters.installation_type.${meter.installation_type}`, {
        ns: "meters",
        defaultValue: meter.installation_type,
      })
    : null;

  return (
    <div>
      <HelpFloatingButtonV2 helpScreen="contracts" />
      <div className="rec-crumbs">
        <Icon name="home" />
        <span>{community?.name || "—"}</span>
        <Icon name="chevron_right" />
        <button
          type="button"
          className="bg-transparent border-0 p-0 cursor-pointer text-inherit font-sans text-[12.5px]"
          onClick={backToMeters}
        >
          {t("title", { ns: "meters" })}
        </button>
        <Icon name="chevron_right" />
        <b>{meter?.identifier || "—"}</b>
      </div>

      <div className="rec-page-head">
        <Button variant="ghost" size="sm" onClick={backToMeters}>
          <Icon name="arrow_back" />
          {t("list.back_button")}
        </Button>
        {meter && (
          <VersionManagementButtons
            communityId={community.id}
            entityType="contract"
            entityId={id}
            label={meter.identifier}
            returnTo={`/communities/${community.id}/infrastructure/meters/${id}`}
          />
        )}
      </div>

      <div className="flex flex-col gap-rec">
        {/* meter hero */}
        <div className="rec-card rec-card-pad">
          <div className="rec-profile-hero">
            <div
              className="rec-av-lg"
              style={{ background: "var(--accent-soft)", color: "var(--accent-deep)" }}
            >
              <Icon name="electric_meter" fill style={{ fontSize: 30 }} />
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-[10px] flex-wrap">
                <h2 className="m-0 text-[21px] font-bold text-ink tracking-[-0.3px] font-[family-name:var(--mono)]">
                  {meter?.identifier || "—"}
                </h2>
                {meter?.type && <Chip tone="teal">{meter.type}</Chip>}
                {instLabel && <Chip tone="gray">{instLabel}</Chip>}
              </div>
              <div className="mt-[6px] text-ink-3 text-[13.5px]">{t("list.title")}</div>
            </div>
          </div>
        </div>

        <div className="rec-card">
          <DataTable
            columns={columns}
            rows={rows}
            onRow={(row) => onEditContractClicked(row.id)}
            search={false}
            emptyText={t("list.empty", { defaultValue: "Sem contratos para este contador." })}
          />
        </div>
      </div>
    </div>
  );
}

export default ContractInformation;
